import moment from "moment";

export interface FileDetails {
	name: string;
	size: number;
	type: string;
}

export const generateTimeSlots = (startTime: string, endTime: string): { value: string; label: string }[] => {
	const timeSlots: { value: string; label: string }[] = [];
	const currentTime = new Date(`2000-01-01T${startTime}`);

	while (currentTime <= new Date(`2000-01-01T${endTime}`)) {
		const hour = currentTime.getHours().toString().padStart(2, '0');
		const minute = currentTime.getMinutes().toString().padStart(2, '0');
		const timeLabel = `${hour}:${minute}`;

		timeSlots.push({ value: timeLabel, label: timeLabel });

		currentTime.setTime(currentTime.getTime() + 60 * 60 * 1000);
	}

	return timeSlots;
};

export const isValidHttpUrl = (string: string) => {
	let url;
	try {
		url = new URL(string);
	} catch (_) {
		return false;
	}
	return url.protocol === 'http:' || url.protocol === 'https:';
};

export const removeExtraSpace = (value: string) => value.trim().split(/ +/).join(' ');

function calculateHoursMinutesSecondsFromSeconds(time: number) {
	const duration = moment.duration(time, 'seconds');
	const hours: any = parseInt(duration.asHours().toString());
	const minutes: number = parseInt(duration.asMinutes().toString()) % 60;
	const seconds: number = parseInt(duration.asSeconds().toString()) % 60;
	return { hours, minutes, seconds };
}

export const calculateAverageHoursOfArray = (array: number[]) => {
	if (!array) {
		return '00:00:00';
	}
	if (array.length === 0) {
		return '00:00:00';
	}
	let sum: any = array.reduce((a, b) => a + b, 0);
	sum = (sum / array.length).toFixed(2);
	const timeData = calculateHoursMinutesSecondsFromSeconds(sum);
	if (parseInt(sum) < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}:${Math.abs(timeData.seconds).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes}:${timeData.seconds}`;
	}
}


export const calculateAverageOfArray = (array: number[], calculate = 'time') => {
	if (array && array.length === 0) {
		return '0.00';
	}
	let sum: any = array.reduce((a, b) => a + b, 0);
	sum = (sum || 0).toFixed(2);
	sum = sum / array.length;
	sum = parseFloat(sum).toFixed(2);
	if (calculate !== 'time') {
		return sum;
	}
	const timeData = calculateHoursMinutesSecondsFromSeconds(sum);
	return `${timeData.hours}:${timeData.minutes}`;
}

// calculate min of array
export const calculateMinOfArray = (array: number[], calculate = 'time') => {
	if (!array.length) {
		return 0;
	}
	if (array.length === 0) {
		return (calculate === 'time') ? '00:00' : '0'
	}
	let min: any = Math.min.apply(null, array);
	if (calculate !== 'time') {
		return min;
	}
	min = (min || 0).toFixed(2);
	const timeData = calculateHoursMinutesSecondsFromSeconds(min);
	// check if min is positive or negative
	if (parseInt(min) < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes.toString().padStart(2, '0')}`;
	}
}

// calculate max of array
export const calculateMaxOfArray = (array: number[], calculate = 'time') => {
	if (!array.length) {
		return 0;
	}
	if (array.length === 0) {
		return (calculate === 'time') ? '00:00' : '0'
	}
	let max: any = Math.max.apply(null, array);
	if (calculate !== 'time') {
		return max;
	}
	max = (max || 0).toFixed(2);
	const timeData = calculateHoursMinutesSecondsFromSeconds(max);
	if (parseInt(max) < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes.toString().padStart(2, '0')}`;
	}
}

export const calculateFirstQuartile = (data: number[], calculate = 'time') => {
	if (!data.length) {
		return 0;
	}
	if (data.length < 4) {
		return (calculate === 'time') ? '00:00' : '0.00'
	}
	data.sort((a, b) => a - b);

	const n = data.length;
	const lowerHalf = data.slice(0, Math.floor(n / 2));
	const q1 = lowerHalf.length % 2 === 0
		? (lowerHalf[lowerHalf.length / 2 - 1] + lowerHalf[lowerHalf.length / 2]) / 2
		: lowerHalf[Math.floor(lowerHalf.length / 2)];
	if (calculate !== 'time') {
		return (q1) ? q1.toFixed(2) : 0.00;
	}

	const timeData = calculateHoursMinutesSecondsFromSeconds(q1);
	if (q1 < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes.toString().padStart(2, '0')}`;
	}

}

export const calculateThirdQuartile = (data: number[], calculate = 'time') => {
	if (!data) {
		return 0;
	}
	if (data.length < 4) {
		return (calculate === 'time') ? '00:00' : '0.00'
	}
	data.sort((a, b) => a - b);
	const n = data.length;
	const upperHalf = data.slice(Math.ceil(n / 2));
	const q3 = upperHalf.length % 2 === 0
		? (upperHalf[upperHalf.length / 2 - 1] + upperHalf[upperHalf.length / 2]) / 2
		: upperHalf[Math.floor(upperHalf.length / 2)];

	if (calculate !== 'time') {
		return (q3) ? q3.toFixed(2) : 0;
	}
	const timeData = calculateHoursMinutesSecondsFromSeconds(q3);
	if (q3 < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes.toString().padStart(2, '0')}`;
	}

}

export const calculateMedian = (data: number[], calculate = 'time') => {
	if (!data) {
		return 0;
	}
	if (data.length === 0) {
		return (calculate === 'time') ? '00:00' : '0.00'
	}
	data.sort((a, b) => a - b);

	const middleIndex = Math.floor(data.length / 2);
	let resultCount: any = 0;
	if (data.length % 2 === 0) {
		const middleValue1 = data[middleIndex - 1];
		const middleValue2 = data[middleIndex];
		resultCount = ((middleValue1 + middleValue2) / 2).toFixed(2);
	} else {
		resultCount = (data[middleIndex]).toFixed(2);
	}
	if (calculate !== 'time') {
		return resultCount;
	}
	const timeData = calculateHoursMinutesSecondsFromSeconds(resultCount);
	if (parseInt(resultCount) < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes.toString().padStart(2, '0')}`;
	}
}

export const calculateStandardDeviation = (data: number[], calculate = 'time') => {
	if (!data) {
		return 0;
	}
	if (data.length === 0) {
		return (calculate === 'time') ? '00:00' : '0.00'
	}
	const mean = data.reduce((sum, value) => sum + value, 0) / data.length;
	const squaredDifferences = data.map(value => Math.pow(value - mean, 2));
	const averageSquaredDifference = squaredDifferences.reduce((sum, value) => sum + value, 0) / data.length;
	const standardDeviation = Math.sqrt(averageSquaredDifference);
	if (calculate !== 'time') {
		return standardDeviation.toFixed(2);
	}
	const timeData = calculateHoursMinutesSecondsFromSeconds(standardDeviation);
	if (standardDeviation < 0) {
		return `-${Math.abs(timeData.hours).toString().padStart(2, '0')}:${Math.abs(timeData.minutes).toString().padStart(2, '0')}`;
	} else {
		return `${timeData.hours.toString().padStart(2, '0')}:${timeData.minutes.toString().padStart(2, '0')}`;
	}
}

export const convertFileToBase64 = (file: File): Promise<string> => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onloadend = () => {
			const base64String = reader.result as string;
			resolve(base64String);
		};
		reader.onerror = (error) => {
			reject(new Error('Error converting file to base64: ' + error));
		};
	});
};